import {
  IonButton,
  IonButtons,
  IonContent,
  IonFooter,
  IonHeader,
  IonIcon,
  IonItem,
  IonItemOption,
  IonItemOptions,
  IonItemSliding,
  IonLabel,
  IonList,
  IonProgressBar,
  IonTitle,
  IonToolbar,
  useIonToast
} from "@ionic/react";
import {useTranslation} from "react-i18next";
import {
  attachOrOcrIncomingDocument,
  createIncomingDocument,
  type IncomingDocumentSupplier,
  type SearchResultIncomingDocument,
  loadSuppliers,
  updateIncomingDocument,
  useIncDocStore
} from "app/employee/state/incdoc/mutations";
import {createSupplier} from "app/employee/state/supplier/mutations";
import {selectMaybeLoaded} from "app/state/common/loadable";
import type {MaybeNil} from "app/utils/types";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {barcodeOutline, checkmarkCircleOutline, closeOutline, eyeOutline, textOutline} from "ionicons/icons";
import {shallow} from "zustand/shallow";
import {getCurrentClientId} from "app/state/currentClient/mutations";
import {assertNotNil, fractionToPercent, isNil, prependExisting} from "app/utils/stdlib";
import {useDefaultLocationId, useEmployeeLocations} from "app/employee/state/bootstrapEmployee/selectors";
import {RhfRequiredLabel} from "app/employee/incdoc/RequiredLabel";
import {AddEditSupplier} from "app/employee/incdoc/supplier/AddEditSupplier";
import {FstIonModal} from "app/employee/incdoc/FstIonModal";
import {HomeBackButton} from "app/employee/menu/HomeBackButton";
import type {IonModalCustomEvent} from "@ionic/core/dist/types/components";
import type {OverlayEventDetail} from "@ionic/react/dist/types/components/react-component-lib/interfaces";
import {useDismiss} from "app/employee/incdoc/useDismiss";
import {linkEmplIncDocHome} from "app/employee/core/employeeLinks";
import {MenuButton} from "app/employee/menu/MenuButton";
import {usePermissions} from "app/employee/hooks/usePermissions";
import type {UseAttachmentCollectionEditorReturn} from "app/components/hooks/useAttachmentCollectionEditor";
import {z} from "zod";
import {useForm} from "react-hook-form";
import {zodResolver} from "@hookform/resolvers/zod";
import {RhfIonInput} from "app/employee/rhf/RhfIonInput";
import {RhfFstIonDatetimeButton} from "app/employee/rhf/RhfFstIonDatetimeButton";
import {RhfIonTextarea} from "app/employee/rhf/RhfIonTextarea";
import {RhfTypeaheadSelect} from "app/employee/rhf/RhfTypeaheadSelect";
import {DateTime} from "luxon";
import {ErrorMessage} from "app/employee/incdoc/supplier/ErrorMessage";
import {type TFunction} from "i18next";
import {zLocalDate, zLocationId, zNormalizedString, zSupplierId} from "app/utils/validator";
import {getLocationText} from "app/employee/utils";
import {useLoadingContext} from "app/employee/LoadingContext";
import {one23Icon} from "app/utils/icons/ionicFixedIcons";
import {AttachmentCollectionEdit} from "app/employee/att/AttachmentCollectionEdit";
import {UploadButton} from "app/employee/att/UploadButton";
import {ListHeaderErrorMessage} from "app/employee/rhf/GlobalErrorMessage";
import {parseErrorResult} from "app/utils/parseErrorResult";
import {Deferred} from "app/utils/deferred";
import type {EditComboBoxOptionOutcome} from "app/employee/controlCard/types";
import type {Refetch} from "app/employee/controlCard/hook/useQuery";
import {
  type LoadingOperations,
  type OcrFieldSetters,
  type SupplierOperations,
  useOcrSuggestion
} from "app/components/hooks/useOcrSuggestion";
import {OcrSuggestionPopover} from "app/employee/incdoc/OcrSuggestionPopover";
import {displaySupplier} from "app/components/admin/employee/utils";

interface Props extends Pick<
  UseAttachmentCollectionEditorReturn,
  "updateAttachments" | "unifiedAttachments" | "uploadProgress" | "showFileUpload" | "collectOcrFiles"
> {
  incomingDocument: MaybeNil<SearchResultIncomingDocument>;
}

const makeFormSchema = (t: TFunction) =>
  z.object({
    supplierId: zSupplierId,
    locationId: zLocationId,
    documentNumber: zNormalizedString.pipe(z.string().min(1, t("g.required"))),
    documentDate: zLocalDate,
    notes: zNormalizedString
  });

export type FormData = z.infer<ReturnType<typeof makeFormSchema>>;

const keyExtractor = (o: {key: string}) => o.key;
const textExtractor = (o: {text: string}) => o.text;
const optionMapper = <Value,>(o: MaybeNil<{value: Value}>) => o?.value;

const supplierKeyExtractor = (o: IncomingDocumentSupplier) => o.id.supplierId;
const supplierOptionMapper = (o: MaybeNil<IncomingDocumentSupplier>) => o?.id;

export const AddEditDocument = ({
  unifiedAttachments,
  showFileUpload,
  uploadProgress,
  incomingDocument,
  updateAttachments,
  collectOcrFiles
}: Props) => {
  const {startLoading, finishLoading, showExceptionError} = useLoadingContext();
  const {t} = useTranslation();
  const supplierTextExtractor = useCallback(
    (supplier: IncomingDocumentSupplier) => displaySupplier(t, supplier) ?? "",
    [t]
  );
  const [presentToast] = useIonToast();
  const [inputMode, setInputMode] = useState<"decimal" | "text">("decimal");
  const inputModeSliderRef = useRef<HTMLIonItemSlidingElement | null>(null);
  const documentNumberInputRef = useRef<HTMLIonInputElement | null>(null);
  const supplierRef = useRef<HTMLIonModalElement | null>(null);
  const [supplierDefaults, setSupplierDefaults] = useState<{name: string} | undefined>(undefined);
  const [supplier, setSupplier] = useState<IncomingDocumentSupplier | null>(null);
  const backHandler = useDismiss(linkEmplIncDocHome());
  const [defaultLocationId, setDefaultLocationId] = useDefaultLocationId();

  const formSchema = useMemo(() => makeFormSchema(t), [t]);

  const {
    control,
    handleSubmit,
    watch,
    formState: {isSubmitting, errors},
    setError,
    setValue
  } = useForm<FormData>({
    defaultValues: {
      supplierId: incomingDocument?.supplier.id,
      locationId: incomingDocument?.location?.id ?? defaultLocationId ?? undefined,
      documentNumber: incomingDocument?.documentNumber ?? "",
      documentDate: incomingDocument?.documentDate ?? DateTime.now().toISODate(),
      notes: incomingDocument?.notes ?? ""
    },
    resolver: zodResolver(formSchema)
  });

  const {canAddIncDocLocation} = usePermissions();
  const locations = prependExisting(
    useEmployeeLocations().filter((location) => canAddIncDocLocation(location.id)),
    incomingDocument?.location
  );

  const {availableSuppliers} = useIncDocStore(
    (state) => ({
      availableSuppliers: selectMaybeLoaded(state.suppliers) ?? []
    }),
    shallow
  );
  const suppliers = prependExisting(availableSuppliers, incomingDocument?.supplier);

  useEffect(() => {
    if (incomingDocument) {
      assertNotNil(incomingDocument.location);
      setValue("supplierId", incomingDocument.supplier.id);
      setValue("locationId", incomingDocument.location.id);
      setValue("documentNumber", incomingDocument.documentNumber);
      setValue("documentDate", incomingDocument.documentDate);
      setValue("notes", incomingDocument.notes);
    }
  }, [incomingDocument, setValue]);

  const saveDisabled =
    !uploadProgress.completed || isSubmitting || (!incomingDocument && unifiedAttachments.length === 0);

  const supplierOps = useMemo<SupplierOperations>(
    () => ({
      createSupplier: async (input) => {
        const result = await createSupplier(input);
        return result.__typename === "SupplierSuccessResult" ? result.supplier : null;
      },
      refreshSuppliers: loadSuppliers
    }),
    []
  );

  const loadingOps = useMemo<LoadingOperations>(
    () => ({
      startLoading,
      finishLoading,
      showExceptionError
    }),
    [startLoading, finishLoading, showExceptionError]
  );

  const fieldSetters = useMemo<OcrFieldSetters>(
    () => ({
      setSupplierId: (id) => setValue("supplierId", id),
      setDocumentNumber: (num) => setValue("documentNumber", num),
      setDocumentDate: (date) => setValue("documentDate", date)
    }),
    [setValue]
  );

  const {canFillWithAi, ocrSuggestion, handleFillWithAi, openPopover, popoverProps} = useOcrSuggestion({
    collectOcrFiles,
    unifiedAttachments,
    uploadProgress,
    isSubmitting,
    fieldSetters,
    supplierOps,
    loadingOps
  });
  const draftPurchaseDocumentId = ocrSuggestion?.purchaseDocument?.id ?? null;

  const onAddHandler = async (form: FormData) => {
    startLoading();
    try {
      assertNotNil(form.locationId);
      setDefaultLocationId([{id: form.locationId}]);
      const createdDocument = await createIncomingDocument({
        clientId: getCurrentClientId(),
        documentDate: form.documentDate,
        supplierId: form.supplierId,
        locationId: form.locationId,
        documentNumber: form.documentNumber,
        notes: form.notes
      });
      if (createdDocument.__typename === "IncomingDocumentSuccessResult") {
        const collectionId = createdDocument.incomingDocument.attachmentCollection.id;
        await updateAttachments(collectionId);
        const incomingDocumentId = createdDocument.incomingDocument.id;
        await attachOrOcrIncomingDocument({
          incomingDocumentId,
          purchaseDocumentId: draftPurchaseDocumentId
        });
        await presentToast({
          position: "top",
          message: t("incdoc.doc.add-document-success", {defaultValue: "Успешно добавяне на документ"}),
          color: "success",
          duration: 2000
        });
        await backHandler();
      } else {
        parseErrorResult(createdDocument, setError);
      }
    } catch (e) {
      await showExceptionError(e);
    } finally {
      finishLoading();
    }
  };

  const onUpdateHandler = async (form: FormData) => {
    startLoading();
    try {
      assertNotNil(incomingDocument);
      assertNotNil(form.locationId);
      setDefaultLocationId([{id: form.locationId}]);
      const updatedDocument = await updateIncomingDocument({
        id: incomingDocument.id,
        documentDate: form.documentDate,
        supplierId: form.supplierId,
        locationId: form.locationId,
        documentNumber: form.documentNumber,
        notes: form.notes
      });
      if (updatedDocument.__typename === "IncomingDocumentSuccessResult") {
        const collectionId = updatedDocument.incomingDocument.attachmentCollection.id;
        await updateAttachments(collectionId);
        await attachOrOcrIncomingDocument({
          incomingDocumentId: incomingDocument.id,
          purchaseDocumentId: draftPurchaseDocumentId
        });
        await presentToast({
          position: "top",
          message: t("incdoc.doc.update-document-success", {defaultValue: "Успешно редактиране на документ"}),
          color: "success",
          duration: 2000
        });
        await backHandler();
      } else {
        parseErrorResult(updatedDocument, setError);
      }
    } catch (e) {
      await showExceptionError(e);
    } finally {
      finishLoading();
    }
  };

  const setDecimalInputMode = async () => {
    setInputMode("decimal");
    await inputModeSliderRef.current?.close();
    await documentNumberInputRef?.current?.setFocus();
  };

  const setTextInputMode = async () => {
    setInputMode("text");
    await inputModeSliderRef.current?.close();
    await documentNumberInputRef?.current?.setFocus();
  };

  const title = incomingDocument
    ? t("incdoc.doc.edit-document-title", {defaultValue: "Редактиране на документ"})
    : t("incdoc.doc.add-document-title", {defaultValue: "Добавяне на документ"});

  const onSaveHandler = incomingDocument ? onUpdateHandler : onAddHandler;

  const supplierEditDeferred = useRef<Deferred<EditComboBoxOptionOutcome<IncomingDocumentSupplier>> | null>(null);

  const refetchSuppliers = useCallback<Refetch>(async (e) => {
    try {
      await loadSuppliers();
    } finally {
      e?.detail.complete();
    }
  }, []);

  const handleSupplierModalDismiss = useCallback(
    async (e: IonModalCustomEvent<OverlayEventDetail<MaybeNil<IncomingDocumentSupplier>>>) => {
      const folder = e.detail.data;
      if (!isNil(folder)) {
        supplierEditDeferred.current?.resolve({
          success: true,
          option: folder
        });
      } else {
        supplierEditDeferred.current?.resolve({
          success: false
        });
      }
      supplierEditDeferred.current = null;
    },
    []
  );

  const handleOnCreate = async (searchText: MaybeNil<string>) => {
    if (supplierEditDeferred.current) {
      supplierEditDeferred.current?.resolve({
        success: false
      });
    }
    supplierEditDeferred.current = new Deferred<EditComboBoxOptionOutcome<IncomingDocumentSupplier>>();
    setSupplierDefaults({name: searchText ?? ""});
    setSupplier(null);
    await supplierRef.current?.present();
    return supplierEditDeferred.current.promise;
  };

  const handleOnEdit = async (supplier: IncomingDocumentSupplier) => {
    if (supplierEditDeferred.current) {
      supplierEditDeferred.current?.resolve({
        success: false
      });
    }
    supplierEditDeferred.current = new Deferred<EditComboBoxOptionOutcome<IncomingDocumentSupplier>>();
    setSupplierDefaults(undefined);
    setSupplier(supplier);
    await supplierRef.current?.present();
    return supplierEditDeferred.current.promise;
  };

  return (
    <>
      <IonHeader>
        <IonToolbar>
          <HomeBackButton />
          <IonTitle>{title}</IonTitle>
          <MenuButton />
        </IonToolbar>
        <IonProgressBar type="determinate" value={uploadProgress.completed ? 1 : uploadProgress.progress} />
      </IonHeader>
      <IonContent>
        <FstIonModal onIonModalDidDismiss={handleSupplierModalDismiss} ref={supplierRef}>
          <AddEditSupplier defaults={supplierDefaults} supplier={supplier} />
        </FstIonModal>
        <OcrSuggestionPopover {...popoverProps} />
        <IonList>
          <ListHeaderErrorMessage errors={errors} />
          <RhfTypeaheadSelect
            keyExtractor={keyExtractor}
            optionMapper={optionMapper}
            options={locations.map((location) => {
              return {
                key: location.id.locationId,
                value: location.id,
                text: getLocationText(location)
              };
            })}
            required
            rhf={{control, name: "locationId"}}
            textExtractor={textExtractor}
            title={t("incdoc.doc.location", {defaultValue: "Обект"})}
          />
          {ocrSuggestion === null ? (
            <IonItem color="highlight" disabled={!canFillWithAi}>
              <IonLabel>{t("incdoc.doc.ai-fill-label", {defaultValue: "Попълни с AI"})}</IonLabel>
              <IonButton disabled={!canFillWithAi} onClick={handleFillWithAi} slot="end">
                <IonIcon icon={barcodeOutline} slot="start" />
                {!uploadProgress.completed
                  ? t("g.uploading", {
                      defaultValue: "Качване ({{uploadProgress}})",
                      uploadProgress: fractionToPercent(uploadProgress.progress)
                    })
                  : t("incdoc.doc.ai-fill-button", {defaultValue: "Стартирай"})}
              </IonButton>
            </IonItem>
          ) : (
            <IonItem color={popoverProps.hasNoResults ? "incomplete" : "completed"} disabled={!canFillWithAi}>
              <IonLabel>
                {popoverProps.hasNoResults
                  ? t("incdoc.doc.ai-no-results-label", {defaultValue: "Не разпознахме данни с AI"})
                  : t("incdoc.doc.ai-suggestions-label", {defaultValue: "Разпознахме документа с AI"})}
              </IonLabel>
              {!popoverProps.hasNoResults && (
                <IonButton onClick={openPopover} slot="end">
                  <IonIcon icon={eyeOutline} slot="start" />
                  {t("incdoc.doc.ai-suggestions-button", {defaultValue: "Преглед"})}
                </IonButton>
              )}
            </IonItem>
          )}
          <RhfTypeaheadSelect
            keyExtractor={supplierKeyExtractor}
            onCreate={handleOnCreate}
            onEdit={handleOnEdit}
            optionMapper={supplierOptionMapper}
            options={suppliers}
            refetch={refetchSuppliers}
            required
            rhf={{control, name: "supplierId"}}
            textExtractor={supplierTextExtractor}
            title={t("incdoc.doc.supplier", {defaultValue: "Доставчик"})}
          />
          <IonItemSliding ref={inputModeSliderRef}>
            <IonItem>
              <RhfIonInput
                inputMode={inputMode}
                labelPlacement="stacked"
                onSubmitValue={async () => {
                  const input = await documentNumberInputRef?.current?.getInputElement();
                  input?.blur();
                }}
                placeholder={t("incdoc.doc.doc-num-placeholder", {defaultValue: "Въведете номерът на документа"})}
                ref={documentNumberInputRef}
                rhf={{control, name: "documentNumber"}}
              >
                <div slot="label">
                  {t("incdoc.doc.doc-num", {defaultValue: "Номер на документ"})}{" "}
                  <RhfRequiredLabel errors={errors} field="documentNumber" watch={watch} />
                </div>
              </RhfIonInput>
            </IonItem>
            <IonItemOptions side="start">
              <IonItemOption color={inputMode === "decimal" ? "secondary" : "light"} onClick={setDecimalInputMode}>
                <IonIcon slot="icon-only" src={one23Icon} />
              </IonItemOption>
              <IonItemOption color={inputMode === "text" ? "secondary" : "light"} onClick={setTextInputMode}>
                <IonIcon icon={textOutline} slot="icon-only" />
              </IonItemOption>
            </IonItemOptions>
          </IonItemSliding>
          <IonItem>
            <IonLabel>
              {t("incdoc.doc.doc-date", {defaultValue: "Дата на документа"})}{" "}
              <RhfRequiredLabel errors={errors} field="documentDate" watch={watch} />
              {errors.documentDate ? (
                <p>
                  <ErrorMessage>{errors.documentDate.message}</ErrorMessage>
                </p>
              ) : null}
            </IonLabel>
            <RhfFstIonDatetimeButton
              multiple={false}
              presentation="date"
              rhf={{control, name: "documentDate"}}
              slot="end"
            >
              <IonLabel slot="title">{t("incdoc.doc.doc-date")}</IonLabel>
            </RhfFstIonDatetimeButton>
          </IonItem>
          <IonItem lines="full">
            <IonLabel>
              <AttachmentCollectionEdit
                disabled={isSubmitting}
                showFileUpload={showFileUpload}
                unifiedAttachments={unifiedAttachments}
              />
            </IonLabel>
          </IonItem>
          <IonItem>
            <RhfIonTextarea
              label={t("incdoc.doc.notes", {defaultValue: "Бележки"})}
              labelPlacement="floating"
              placeholder={t("incdoc.doc.notes-placeholder", {defaultValue: "Допълнителни бележки (ако има нужда)"})}
              rhf={{control, name: "notes"}}
            />
          </IonItem>
        </IonList>
      </IonContent>
      <IonFooter>
        <IonToolbar>
          <IonButtons slot="primary">
            <IonButton color="primary" fill="outline" onClick={() => backHandler()}>
              <IonIcon icon={closeOutline} slot="start" />
              {t("g.cancel", {defaultValue: "Откажи"})}
            </IonButton>
            <UploadButton
              color="secondary"
              disabled={isSubmitting}
              fill="solid"
              showFileUpload={showFileUpload}
              unifiedAttachments={unifiedAttachments}
            />
            <IonButton color="primary" disabled={saveDisabled} fill="solid" onClick={handleSubmit(onSaveHandler)}>
              <IonIcon icon={checkmarkCircleOutline} slot="start" />
              {!uploadProgress.completed
                ? t("g.uploading", {
                    defaultValue: "Качване ({{uploadProgress}})",
                    uploadProgress: fractionToPercent(uploadProgress.progress)
                  })
                : t("g.save", {defaultValue: "Запази"})}
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonFooter>
    </>
  );
};
